'use client';

import { useCallback } from 'react';
import { usePathname } from '@/i18n/routing';
import { locales } from '@/i18n/locales';

const NEWEST_SEGMENT = '/newest';
const NEWEST_SORT = 'newest';
const DEFAULT_SORT = 'featured';

// Optionally locale-prefixed /c/<slug>, /brands/<slug>, /categories or /categories/newest,
// optionally followed by the /page/N pagination segment or the /search twin segment. Mirrors the
// route shapes handled by `withListingRewrite` on the server.
const LISTING_ROUTE_RE = new RegExp(
  `^(?:/(?:${locales.join('|')}))?(?:/c/[^/]+|/brands/[^/]+|(?<categories>/categories)(?<newest>${NEWEST_SEGMENT})?)(?=/page/\\d+/?$|/search/?$|/?$)`,
);

/**
 * Builds hrefs for client-side listing navigations (sort, filters).
 *
 * On the listing routes the href targets the dynamic twin (`<base>/search?params`) when listing
 * params are present, and the static base path when none remain: the Next.js client cache reuses a
 * static payload for any search params on the same pathname, so `router.replace('<base>?params')`
 * can render the unfiltered static page; `withListingRewrite` serves direct `<base>?params` loads.
 *
 * /categories has two static bases, /categories (featured) and /categories/newest: a bare sort
 * choice between them lands on one of them; any other sort or filter goes to the twin.
 *
 * On every other route (e.g. /search) the href keeps the current pathname — those routes are
 * dynamic and render their own search params.
 */
export function useListingNavigation(): {
  buildListingHref: (params: URLSearchParams) => string;
} {
  const pathname = usePathname() || '/';

  const buildListingHref = useCallback(
    (params: URLSearchParams) => buildListingHrefFor(pathname, params),
    [pathname],
  );

  return { buildListingHref };
}

/**
 * `buildListingHref` for a given pathname, with or without a locale prefix (kept as given), for
 * links built from next-intl's locale-less pathname (the language switcher).
 */
export function buildListingHrefFor(pathname: string, params: URLSearchParams): string {
  const qs = params.toString();
  const match = pathname.match(LISTING_ROUTE_RE);
  if (!match) {
    return qs ? `${pathname}?${qs}` : pathname;
  }

  const impliedNewest = !!match.groups?.newest;
  const listingBase = impliedNewest ? match[0].slice(0, -NEWEST_SEGMENT.length) : match[0];

  if (match.groups?.categories) {
    const onlySort = [...params.keys()].every((key) => key === 'sort');
    const sort = params.get('sort') ?? (impliedNewest ? NEWEST_SORT : DEFAULT_SORT);
    if (onlySort && sort === NEWEST_SORT) return `${listingBase}${NEWEST_SEGMENT}`;
    if (onlySort && sort === DEFAULT_SORT) return listingBase;
  }

  return qs ? `${listingBase}/search?${qs}` : listingBase;
}
